// Copyright (c) 2026, Cyber Cube and contributors
// For license information, please see license.txt
//
// The module's DOM toolkit. Every screen in Facility Asset Inspections builds
// its markup through these helpers, and the reason is one rule:
//
//   **`.text()`, never `.html()`, for anything a human typed.**
//
// jQuery `.html()` on a template literal built from `get_list` results executes
// any markup in the data (ARCHITECTURE.md §10.24). Both shipped grid engines are
// exactly that sink, which is why CONVENTIONS #17 says hand-rolled read-only
// rendering beats a `qhse.table` config for a new module. This module renders
// asset tags, asset names, zone names, `zone_path` breadcrumbs, question text,
// verdict reasons and free-text comments — every one of them author- or
// inspector-supplied — so there is no call site where `.html()` with data would
// be acceptable and no helper here offers one.
//
// `el()` therefore takes its content as **text** and sets it with `textContent`.
// The only markup this file emits is `icon()`, whose SVG paths are literals in
// this file and contain nothing caller-controlled.

frappe.provide("qhse.facility");

// 24-viewBox stroke paths, matching `qhse/setup/platform_home.py::_ICONS`.
//
// `stroke="currentColor"`, never `stroke: var(--primary)`: frappe defines
// `--primary` as a near-black in BOTH themes, and forcing it as a stroke once
// painted seven of eight Observations icons invisible on the dark desk
// (ARCH §10.30). Explicit `width`/`height` attributes for the other half of the
// same gotcha — an SVG carrying only a viewBox has no intrinsic size.
const ICONS = {
	asset: "M3 8.5L12 4l9 4.5v7L12 20l-9-4.5zM3 8.5L12 13l9-4.5M12 13v7",
	zone: "M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11zM12 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5",
	plan: "M8 3v3M16 3v3M4 8.5h16M5 5.5h14a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6.5a1 1 0 0 1 1-1M9 13h2M9 17h2M14 13h2",
	template:
		"M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2M9 8h6M9 12h6M9 16h3",
	round: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16.5 16.5L21 21",
	qr: "M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h2v2h-2zM18 14h2v2h-2zM14 18h2v2h-2zM18 18h2v2h-2z",
	clock: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2",
	warning:
		"M10.3 3.9L1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0zM12 9v4.5M12 17.5h.01",
	check: "M20 6L9 17l-5-5",
	back: "M19 12H5M12 19l-7-7 7-7",
	// M9.9b. `caret` is the tree expander, `grip` the builder's drag handle,
	// `add` / `remove` the scope-row buttons, `list` the exclusion modal's
	// `[view list]`, `resync` the re-sync banner's action.
	caret: "M9 6l6 6-6 6",
	grip: "M9 6h.01M9 12h.01M9 18h.01M15 6h.01M15 12h.01M15 18h.01",
	add: "M12 5v14M5 12h14",
	remove: "M5 12h14",
	list: "M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01",
	report: "M4 20h16M7 20v-6M12 20V8M17 20v-9",
	resync: "M21 12a9 9 0 1 1-2.6-6.4M21 3v5h-5",
	// M9.13. `checklist` is the Inspections nav entry — `round` is the module
	// overview's and two identical glyphs in one sidebar is a sidebar nobody can
	// scan. `clock` above already carries Schedules. Not in
	// `platform_home.py::_ICONS`: that map is the launcher's and this key has no
	// tile.
	checklist:
		"M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1M8 6H6.5A1.5 1.5 0 0 0 5 7.5v12A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-12A1.5 1.5 0 0 0 17.5 6H16M8.5 12l1.5 1.5 3-3M8.5 17l1.5 1.5 3-3",
};

// The `QHSE Zone Kind` master's `icon` column holds a **key**, never markup
// (ARCH §6.17): storing raw SVG in a table any manager can edit and injecting it
// into the page is §10.24's stored-XSS sink. The key is resolved here, against
// paths that are literals in this file, and an unknown key falls back to the
// generic `zone` pin rather than rendering nothing — a kind an operator invented
// still gets a node marker.
//
// Every path is a 24-viewBox stroke path, drawn with `createElementNS` and
// `stroke="currentColor"` for the same reason as the set above (§10.30).
const ZONE_KIND_ICONS = {
	site: "M3 20h18M5 20V9l7-5 7 5v11M10 20v-6h4v6",
	building: "M6 21V4h12v17M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2",
	floor: "M3 9h18M3 15h18M3 4.5h18v15H3z",
	room: "M4 4h16v16H4zM15.5 12h.01",
	area: "M4 7.5l8-3.5 8 3.5v9L12 20l-8-3.5z",
	yard: "M3 19h18M6 19v-6M12 19V8M18 19v-9M4.5 13h3M10.5 8h3M16.5 10h3",
	plant: "M4 20V10l4-3 4 3V6l4-2 4 2v14M8 20v-4h4v4",
	warehouse: "M3 20V10l9-5 9 5v10M8 20v-7h8v7M8 16.5h8",
	workshop: "M14.5 4.5l5 5-2.5 2.5-5-5zM11 8l-7 7v5h5l7-7",
	office: "M5 21V3h9v18M14 9h5v12M8 7h3M8 11h3M8 15h3M17 13h.01M17 17h.01",
};

// A zone kind may carry a colour, and it reaches a `style` attribute — so it is
// validated here rather than trusted. `QHSE Zone Kind.colour` is a `Data` column
// any manager can type into, and an unvalidated value in a style attribute is a
// CSS-injection surface (ARCH §6.17 states the obligation; this is where it is
// met). Anything that is not exactly six hex digits is dropped, which renders
// the node in the inherited colour.
const HEX = /^#[0-9a-fA-F]{6}$/;

// The asset register's inspection glyphs. `last_inspection_verdict` is the only
// per-asset outcome the register carries (ARCH §6.20), so the mapping is stated
// here rather than guessed per screen, and the glyph's `title` names the real
// value — `PHASE-9.md` §8.2 shows `✓ / ⚠ / ○` and this is what they mean.
const VERDICT_GLYPHS = {
	Pass: { glyph: "✓", tone: "ok" },
	"Pass with Observations": { glyph: "⚠", tone: "warn" },
	Fail: { glyph: "⚠", tone: "bad" },
};

const NEVER_INSPECTED = { glyph: "○", tone: "idle" };

qhse.facility.render = {
	ICONS,
	ZONE_KIND_ICONS,
	VERDICT_GLYPHS,
	NEVER_INSPECTED,

	/** An element. `content` is TEXT — it is never parsed as markup. */
	el(tag, attrs, content) {
		const node = document.createElement(tag);
		Object.keys(attrs || {}).forEach((key) => {
			const value = attrs[key];
			if (value === null || value === undefined || value === false) return;
			if (key === "class") node.className = value;
			else if (key === "dataset") {
				Object.keys(value).forEach((d) => {
					node.dataset[d] = value[d];
				});
			} else node.setAttribute(key, value);
		});
		if (content !== null && content !== undefined && content !== "") {
			// **A Node is appended, never stringified (M9.33).**
			//
			// `String(aTable)` is `"[object HTMLTableElement]"`, which renders as
			// that literal text with the real element silently discarded — no
			// error, no empty state, just a line of prose where a table should
			// be. It cost an hour here: `el("div", {class: "…-table-wrap"},
			// table())` produced a wrapper containing the words and no rows, and
			// every count the probe took was legitimately zero.
			//
			// Appending is strictly more useful and can break nothing: a caller
			// that passed a Node before was already broken, visibly.
			if (content instanceof Node) node.appendChild(content);
			else if (Array.isArray(content)) this.append(node, ...content);
			else node.textContent = String(content);
		}
		return node;
	},

	/** Append any number of children, skipping null. */
	append(parent, ...children) {
		children.forEach((child) => {
			if (child) parent.appendChild(child);
		});
		return parent;
	},

	/** Replace a node's children. Never `.html("")` — no parse, no sink. */
	clear(node) {
		while (node.firstChild) node.removeChild(node.firstChild);
		return node;
	},

	/** CONVENTIONS #20: an unreadable count is an em dash; a real zero is `0`.
	 *
	 * The server already returns display strings for every count it owns, so
	 * this is for values read straight off a row — where `null` means the column
	 * is empty and `0` means it is genuinely zero.
	 */
	dash(value) {
		if (value === null || value === undefined || value === "") return "—";
		return String(value);
	},

	/** A zone breadcrumb, shortened to its tail, with the whole path on hover.
	 *
	 * M9.35. The Mouwasat estate is six levels deep — *Mouwasat › Dammam ›
	 * Mouwasat Hospital Dammam › Main Tower › First Floor › Intensive Care Unit*
	 * — and a list cell holding that wraps to eight lines on a 390px screen.
	 * Three list rows measured 261–280px against a 260px cap the moment the data
	 * got realistic, which is the harness catching a rendering rule that had only
	 * ever seen three-hop paths.
	 *
	 * The tail is the answer rather than a smaller font or a wider cap: on a
	 * phone the reader already knows which hospital they are in, and *First Floor
	 * › Intensive Care Unit* is the part that locates the asset. The full path
	 * stays in `title`, so nothing is lost — it is one hover or one long-press
	 * away, and the asset's own page still prints it whole.
	 */
	// NOT `.qhse-fac-trail`: that is `zones.js`'s breadcrumb CONTAINER — a flex
	// row of hop chips with kind icons and a 10px bottom margin — and this is an
	// inline span inside a table cell. Same family, different element, and a
	// shared class with two layouts is the M9.17 defect. Caught by
	// `TestNoScreenBorrowsAnothersClassName` before it shipped, for the fifth
	// time.
	trail(value, keep) {
		const hops = String(value || "").split("\u203a").map((h) => h.trim()).filter(Boolean);
		const wanted = keep || 2;
		const node = this.el("span", { class: "qhse-fac-zonetrail" });
		if (!hops.length) {
			node.textContent = this.dash(value);
			return node;
		}
		node.textContent = hops.slice(-wanted).join(" \u203a ");
		if (hops.length > wanted) {
			// The whole path, for anyone who needs it. `title` is an attribute
			// set through `el`, so a zone name containing markup stays inert.
			node.setAttribute("title", hops.join(" \u203a "));
			node.classList.add("is-trimmed");
		}
		return node;
	},

	/** An inline SVG icon.
	 *
	 * `width`/`height` are attributes, not CSS: this markup is also cloned into
	 * a workspace Custom HTML Block, which renders inside a Shadow DOM that
	 * `app_include_css` cannot reach (ARCH §10.30).
	 */
	icon(key, size) {
		const path = ICONS[key];
		if (!path) return null;
		// One builder, shared with `zoneIcon()`. A second copy of the attribute
		// list is a second chance to forget `width`/`height` or to reach for
		// `var(--primary)` (CONVENTIONS #24, ARCH §10.30).
		return this._svg(path, size || 18);
	},

	/** CONVENTIONS #21: the section rule — 11px/600/uppercase over a hairline. */
	sectionTitle(label) {
		return this.el("h3", { class: "qhse-fac-section-title" }, label);
	},

	section(label, ...children) {
		const wrap = this.el("section", { class: "qhse-fac-section" });
		if (label) wrap.appendChild(this.sectionTitle(label));
		return this.append(wrap, ...children);
	},

	/** A KPI tile. `value` is already a display string from the server.
	 *
	 * `options` is M9.17's: `{tone, onClick, title}`. A tile with an `onClick` is
	 * a real `<button>`, not a div with a listener — it is focusable, it answers
	 * the keyboard, and a screen reader announces it as something that can be
	 * pressed. The band on the module home is entirely buttons, because this
	 * module's own rule is that a number the reader cannot act on is worse than no
	 * number (`LS3`, M9.13): every figure opens the list that defines it.
	 *
	 * `tone` colours the **figure**, never the whole tile: a wall of red boxes
	 * reads as an error state rather than as a priority, and the one number that
	 * matters stops standing out.
	 */
	kpi(key, label, value, options) {
		const opts = options || {};
		const tile = opts.onClick
			? this.el("button", {
					type: "button",
					class: "qhse-fac-kpi is-clickable",
					title: opts.title || null,
			  })
			: this.el("div", { class: "qhse-fac-kpi", title: opts.title || null });
		if (opts.onClick) tile.addEventListener("click", opts.onClick);
		const v = this.el(
			"div",
			{
				class: `qhse-fac-kpi-value${opts.tone ? ` is-${opts.tone}` : ""}`,
				dataset: { qhseMetric: key },
			},
			value === undefined ? "—" : value
		);
		return this.append(tile, v, this.el("div", { class: "qhse-fac-kpi-label" }, label));
	},

	/** A navigation tile. The route is the caller's; no inline handler (§10.3).
	 *
	 * `options.href` (M9.17) is for a tile whose destination is **not** a desk
	 * route — the module's own field portal lives at `/inspect`, a website route
	 * the desk sub-router knows nothing about. It travels as a plain `href` with
	 * no `data-qhse-action`, so the shell's delegated handler leaves it alone and
	 * the browser navigates normally. It is still this module's screen; it is the
	 * shell around it that is different.
	 */
	tile(iconKey, label, route, count, options) {
		const opts = options || {};
		const a = opts.href
			? this.el("a", { class: "qhse-fac-tile", href: opts.href })
			: this.el("a", {
					class: "qhse-fac-tile",
					href: `/app/${route}`,
					dataset: { qhseAction: "route", route },
			  });
		if (count !== undefined && count !== null && count !== "0") {
			a.appendChild(this.el("span", { class: "qhse-fac-tile-count" }, count));
		}
		this.append(a, this.icon(iconKey, 24));
		return this.append(a, this.el("span", { class: "qhse-fac-tile-label" }, label));
	},

	/** The `✓ / ⚠ / ○` glyph for an asset's last verdict, with an honest title. */
	verdictGlyph(verdict) {
		const spec = VERDICT_GLYPHS[verdict] || NEVER_INSPECTED;
		const title = verdict || __("Never inspected");
		return this.el(
			"span",
			{ class: `qhse-fac-glyph is-${spec.tone}`, title },
			spec.glyph
		);
	},

	/** A definition row: label on the left, value on the right, both as text. */
	field(label, value) {
		const row = this.el("div", { class: "qhse-fac-field" });
		return this.append(
			row,
			this.el("span", { class: "qhse-fac-field-label" }, label),
			this.el("span", { class: "qhse-fac-field-value" }, this.dash(value))
		);
	},

	/** A read-only table, built row by row with text only (CONVENTIONS #17).
	 *
	 * `columns` is `[{label, get(row), class}]`. Deliberately not a
	 * `qhse.table` config: both shipped engines interpolate row values into
	 * `.html()` unescaped and their row-button deletes are dead (§9.8, §10.12),
	 * and their configs live in generated files.
	 */
	table(columns, rows, options) {
		const opts = options || {};
		const table = this.el("table", { class: "qhse-fac-table" });
		const thead = this.el("thead");
		const hr = this.el("tr");
		columns.forEach((col) => {
			hr.appendChild(this.el("th", { class: col.class || null }, col.label));
		});
		thead.appendChild(hr);
		table.appendChild(thead);

		const tbody = this.el("tbody");
		rows.forEach((row) => {
			const tr = this.el("tr");
			if (opts.route) {
				const route = opts.route(row);
				if (route) {
					tr.dataset.qhseAction = "route";
					tr.dataset.route = route;
					tr.className = "is-clickable";
				}
			}
			columns.forEach((col) => {
				const td = this.el("td", { class: col.class || null });
				const value = col.get(row);
				if (value instanceof Node) td.appendChild(value);
				else td.textContent = this.dash(value);
				tr.appendChild(td);
			});
			tbody.appendChild(tr);
		});
		table.appendChild(tbody);

		if (!rows.length) {
			const wrap = this.el("div", { class: "qhse-fac-empty" });
			return this.append(
				wrap,
				this.el("p", {}, opts.empty || __("Nothing here yet."))
			);
		}

		// A wide table scrolls inside its own box rather than pushing the page
		// sideways.
		const scroller = this.el("div", { class: "qhse-fac-table-wrap" });
		return this.append(scroller, table);
	},

	/** The honest empty state. A fresh site has 0 assets, by design (M9.10 seeds). */
	empty(message, hint) {
		const wrap = this.el("div", { class: "qhse-fac-empty" });
		this.append(wrap, this.el("p", { class: "qhse-fac-empty-message" }, message));
		if (hint) wrap.appendChild(this.el("p", { class: "qhse-fac-empty-hint" }, hint));
		return wrap;
	},

	// ----------------------------------------------------------- M9.9b widgets

	/** An SVG path from a named map, sized and stroked per §10.30.
	 *
	 * The one primitive `icon()` and `zoneIcon()` both build on, so there is
	 * exactly one place that decides an inline SVG carries explicit `width`,
	 * `height` and `stroke="currentColor"`. Anything else is a second chance to
	 * get §10.30 wrong.
	 */
	_svg(path, px) {
		const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		svg.setAttribute("xmlns", "http://www.w3.org/2000/svg");
		svg.setAttribute("width", px);
		svg.setAttribute("height", px);
		svg.setAttribute("viewBox", "0 0 24 24");
		svg.setAttribute("fill", "none");
		svg.setAttribute("stroke", "currentColor");
		svg.setAttribute("stroke-width", "1.5");
		svg.setAttribute("stroke-linecap", "round");
		svg.setAttribute("stroke-linejoin", "round");
		svg.setAttribute("aria-hidden", "true");
		const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
		p.setAttribute("d", path);
		svg.appendChild(p);
		return svg;
	},

	/** The marker for one `zone_kind`, resolved from the master's `icon` KEY.
	 *
	 * `key` is `QHSE Zone Kind.icon` — data, never markup (ARCH §6.17). An
	 * unknown key falls back to the generic pin so a kind an operator invented
	 * still draws a node; returning null would make the tree lose its alignment
	 * on exactly the rows a site has customised.
	 */
	zoneIcon(key, size) {
		const path = ZONE_KIND_ICONS[String(key || "").trim()] || ICONS.zone;
		return this._svg(path, size || 15);
	},

	/** `QHSE Zone Kind.colour`, or null when it is not exactly six hex digits.
	 *
	 * Validated here rather than trusted: the column is free text a manager can
	 * type into and the value reaches a `style` attribute.
	 */
	colour(value) {
		const hex = String(value || "").trim();
		return HEX.test(hex) ? hex : null;
	},

	/** The planner's step strip — happy path only, current step marked.
	 *
	 * Deliberately **not** `qhse.workflow.render`: that strip draws a *document's
	 * workflow state* (CONVENTIONS #8) and these four steps are a wizard's
	 * position, which is not a state of anything. Reusing it would put a
	 * chevron on screen that means something different from every other chevron
	 * in the app.
	 */
	steps(labels, activeIndex) {
		const strip = this.el("ol", { class: "qhse-fac-steps" });
		labels.forEach((label, index) => {
			const done = index < activeIndex;
			const item = this.el("li", {
				class: `qhse-fac-step${index === activeIndex ? " is-active" : ""}${
					done ? " is-done" : ""
				}`,
				dataset: { qhseStep: String(index) },
			});
			this.append(
				item,
				this.el("span", { class: "qhse-fac-step-dot" }, done ? "✓" : String(index + 1)),
				this.el("span", { class: "qhse-fac-step-label" }, label)
			);
			strip.appendChild(item);
		});
		return strip;
	},

	/** A radio-style pill group. The chosen value lives in the CALLER's state.
	 *
	 * `onPick` is handed the value; nothing is ever read back out of the DOM.
	 * CLAUDE.md §15.6 is the whole reason: a hidden control does not reliably
	 * round-trip `set_value`, and a surface that re-reads its own control to
	 * decide what to show next silently shows the wrong thing.
	 */
	pills(values, active, onPick) {
		const wrap = this.el("div", { class: "qhse-fac-pills", role: "radiogroup" });
		values.forEach((entry) => {
			const value = Array.isArray(entry) ? entry[0] : entry;
			const label = Array.isArray(entry) ? entry[1] : entry;
			const pill = this.el(
				"button",
				{
					type: "button",
					class: `qhse-fac-pill${value === active ? " is-active" : ""}`,
					role: "radio",
					"aria-checked": value === active ? "true" : "false",
				},
				label
			);
			pill.addEventListener("click", () => onPick(value));
			wrap.appendChild(pill);
		});
		return wrap;
	},

	/** A labelled `<select>` whose options came from the schema (§10.13). */
	select(label, options, value, onChange, extra) {
		const wrap = this.el("label", { class: "qhse-fac-rail-field" });
		if (label) {
			wrap.appendChild(this.el("span", { class: "qhse-fac-rail-label" }, label));
		}
		const control = this.el("select", { class: "qhse-fac-input" });
		((extra && extra.blank) !== false
			? [["", (extra && extra.blankLabel) || __("Any")]]
			: []
		)
			.concat(options.map((o) => (Array.isArray(o) ? o : [o, o])))
			.forEach(([optionValue, optionLabel]) => {
				const option = this.el("option", { value: optionValue }, optionLabel);
				if (String(optionValue) === String(value === null || value === undefined ? "" : value)) {
					option.selected = true;
				}
				control.appendChild(option);
			});
		control.addEventListener("change", () => onChange(control.value));
		wrap.appendChild(control);
		wrap.control = control;
		return wrap;
	},

	/** Replace a `select()`'s options in place, keeping its label and handler.
	 *
	 * M9.27. The register's create dialog filters asset types by category, and a
	 * filter that rebuilt the whole control would lose the `change` listener the
	 * caller attached. `textContent` per option, never markup (§10.24) — an asset
	 * type name is operator-typed.
	 */
	fillSelect(wrap, options, blankLabel) {
		const control = wrap && wrap.control;
		if (!control) return wrap;
		control.textContent = "";
		[["", blankLabel || __("Any")]]
			.concat((options || []).map((o) => (Array.isArray(o) ? o : [o, o])))
			.forEach(([optionValue, optionLabel]) => {
				control.appendChild(this.el("option", { value: optionValue }, optionLabel));
			});
		control.value = "";
		return wrap;
	},

	/** A labelled text/number/date input. */
	input(label, type, value, onChange, extra) {
		const wrap = this.el("label", { class: "qhse-fac-rail-field" });
		if (label) {
			wrap.appendChild(this.el("span", { class: "qhse-fac-rail-label" }, label));
		}
		const control = this.el("input", {
			class: "qhse-fac-input",
			type: type || "text",
			placeholder: (extra && extra.placeholder) || null,
		});
		control.value = value === null || value === undefined ? "" : String(value);
		control.addEventListener("change", () => onChange(control.value));
		if (extra && extra.live) {
			control.addEventListener("input", () => onChange(control.value));
		}
		wrap.appendChild(control);
		wrap.control = control;
		return wrap;
	},

	/** A labelled multi-line input, for the `Small Text` fields.
	 *
	 * Its own helper rather than `input(label, "text", …)` because a one-line box
	 * for a notes field is the shape of "least work that passes" the audit's §11.2
	 * is a register of. Same contract as `input`: the value travels to `onChange`
	 * and is never read back out of the control (§15.6).
	 */
	textarea(label, value, onChange, extra) {
		const wrap = this.el("label", { class: "qhse-fac-rail-field" });
		if (label) {
			wrap.appendChild(this.el("span", { class: "qhse-fac-rail-label" }, label));
		}
		const control = this.el("textarea", {
			class: "qhse-fac-input qhse-fac-textarea",
			rows: String((extra && extra.rows) || 3),
			placeholder: (extra && extra.placeholder) || null,
		});
		// `.value`, never `.innerHTML` — a notes field is operator-typed and this
		// is exactly §10.24's sink if it were filled as markup.
		control.value = value === null || value === undefined ? "" : String(value);
		control.addEventListener("change", () => onChange(control.value));
		control.addEventListener("input", () => onChange(control.value));
		wrap.appendChild(control);
		wrap.control = control;
		return wrap;
	},

	/** A checkbox with its label to the right. `onChange` gets 0 or 1. */
	check(label, checked, onChange) {
		const wrap = this.el("label", { class: "qhse-fac-check" });
		const box = this.el("input", { type: "checkbox" });
		box.checked = !!checked;
		box.addEventListener("change", () => onChange(box.checked ? 1 : 0));
		this.append(wrap, box, this.el("span", {}, label));
		wrap.control = box;
		return wrap;
	},

	/** A small icon button. Never an inline handler (§10.3). */
	iconButton(iconKey, title, onClick, extra) {
		const button = this.el("button", {
			type: "button",
			class: `qhse-fac-icon-button${extra && extra.class ? ` ${extra.class}` : ""}`,
			title,
			"aria-label": title,
		});
		const glyph = this.icon(iconKey, (extra && extra.size) || 15);
		if (glyph) button.appendChild(glyph);
		button.addEventListener("click", onClick);
		return button;
	},

	/** An inline notice. `tone` is `info`, `warn` or `bad`; `actions` are nodes. */
	banner(tone, title, detail, actions) {
		const wrap = this.el("div", { class: `qhse-fac-banner is-${tone}` });
		const body = this.el("div", { class: "qhse-fac-banner-body" });
		this.append(body, this.el("strong", {}, title));
		if (detail) body.appendChild(this.el("span", {}, detail));
		const glyph = this.icon(tone === "info" ? "clock" : "warning", 16);
		if (glyph) wrap.appendChild(glyph);
		wrap.appendChild(body);
		if (actions && actions.length) {
			const bar = this.el("div", { class: "qhse-fac-banner-actions" });
			this.append(bar, ...actions);
			wrap.appendChild(bar);
		}
		return wrap;
	},

	/** A `frappe.ui.Dialog` shell with a hand-rendered body (ARCH §8.6).
	 *
	 * The shell is used for its backdrop, focus trap and native stacking; the
	 * body is ours because frappe has no control for a searchable tick-list. The
	 * caller gets `{dialog, body, footer, setPrimary}` and never reads a value
	 * back out of a frappe control (§15.6).
	 *
	 * **`frappe.ui.Dialog` does not exist on the portal** (M9.9c).
	 * `frappe/public/js/frappe-web.bundle.js` imports `ui/messages.js` — which is
	 * why `frappe.show_alert` works there — but never `ui/dialog.js`, so the class
	 * the desk takes for granted is simply absent on `/inspect*`. It is absent in
	 * the way §10.13 and CONVENTIONS #2 keep warning about: `new frappe.ui.Dialog`
	 * throws a `TypeError` that the caller's `catch` turns into a screen saying
	 * "this could not be rendered", with nothing naming the real cause.
	 *
	 * So the fallback below is a hand-rendered overlay with the **same three-key
	 * return shape** and the same `show`/`hide` methods. One modal API for the
	 * whole module, in both shells, rather than a second composer written for the
	 * phone (CONVENTIONS #24).
	 *
	 * Both arms append to `<body>`, **outside** `.qhse-fac` / `.qhse-inspect` — so
	 * every rule that styles a modal is declared at the stylesheet's top level, not
	 * nested under the page class. Same class of mistake as §10.30's shadow root,
	 * reached through a different boundary (M9.9b's handoff names it).
	 */
	dialog(title, primaryLabel, onPrimary) {
		if (!(window.frappe && frappe.ui && frappe.ui.Dialog)) {
			return this._plainDialog(title, primaryLabel, onPrimary);
		}
		const dialog = new frappe.ui.Dialog({
			title,
			size: "large",
			primary_action_label: primaryLabel,
			primary_action: () => onPrimary(dialog),
		});
		const host = dialog.$wrapper.find(".modal-body");
		host.empty();
		const body = this.el("div", { class: "qhse-fac-modal" });
		host.append(body);
		return {
			dialog,
			body,
			setPrimary(enabled, reason) {
				const button = dialog.get_primary_btn();
				button.prop("disabled", !enabled);
				// The reason travels in `title`, never as a validation error on
				// submit — PHASE-9.md §8.5's rule, applied everywhere.
				button.attr("title", enabled ? "" : reason || "");
			},
		};
	},

	/** The portal's modal — same shape, no `frappe.ui.Dialog`.
	 *
	 * Built with `el()` like everything else here, so the title and the button
	 * labels are `textContent` and there is no markup path (§10.24). Escape and a
	 * click on the backdrop close it, because a fixed overlay with no exit on a
	 * phone is a page an inspector has to reload out of.
	 */
	_plainDialog(title, primaryLabel, onPrimary) {
		const overlay = this.el("div", { class: "qhse-fac-overlay", role: "dialog" });
		const panel = this.el("div", { class: "qhse-fac-overlay-panel" });
		const head = this.el("div", { class: "qhse-fac-overlay-head" });
		const body = this.el("div", { class: "qhse-fac-modal" });
		const foot = this.el("div", { class: "qhse-fac-overlay-foot" });

		const api = {
			show() {
				document.body.appendChild(overlay);
				document.addEventListener("keydown", onKey);
			},
			hide() {
				document.removeEventListener("keydown", onKey);
				if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
			},
		};
		function onKey(event) {
			if (event.key === "Escape") api.hide();
		}

		const close = this.el("button", { type: "button", class: "qhse-fac-overlay-close",
			title: __("Close"), "aria-label": __("Close") }, "×");
		close.addEventListener("click", () => api.hide());
		this.append(head, this.el("strong", {}, title), close);

		const primary = this.el(
			"button",
			{ type: "button", class: "qhse-fac-button is-primary" },
			primaryLabel
		);
		primary.addEventListener("click", () => onPrimary(api));
		const cancel = this.el(
			"button",
			{ type: "button", class: "qhse-fac-button is-ghost" },
			__("Cancel")
		);
		cancel.addEventListener("click", () => api.hide());
		this.append(foot, cancel, primary);

		this.append(panel, head, body, foot);
		overlay.appendChild(panel);
		overlay.addEventListener("click", (event) => {
			if (event.target === overlay) api.hide();
		});

		return {
			dialog: api,
			body,
			setPrimary(enabled, reason) {
				primary.disabled = !enabled;
				primary.setAttribute("title", enabled ? "" : reason || "");
			},
		};
	},

	/** A collapsible card. `onToggle` is told the new open state.
	 *
	 * The body stays in the DOM when closed and is hidden with the `hidden`
	 * attribute — but note the builder **expands every card while a drag is in
	 * flight** (see `builder.js`): a zero-height list cannot be a drop target,
	 * and §10.31 is exactly the lesson that a hidden element measures zero while
	 * every check written against it reports green.
	 */
	card(titleNode, count, children, options) {
		const opts = options || {};
		const wrap = this.el("section", { class: "qhse-fac-card" });
		const head = this.el("div", { class: "qhse-fac-card-head" });
		const caret = this.el("button", {
			type: "button",
			class: `qhse-fac-card-caret${opts.open === false ? "" : " is-open"}`,
			"aria-expanded": opts.open === false ? "false" : "true",
			title: __("Expand or collapse"),
		});
		const glyph = this.icon("caret", 14);
		if (glyph) caret.appendChild(glyph);
		this.append(head, caret, titleNode);
		if (count !== undefined && count !== null) {
			head.appendChild(this.el("span", { class: "qhse-fac-card-count" }, String(count)));
		}
		if (opts.tools) this.append(head, ...opts.tools);
		const body = this.el("div", { class: "qhse-fac-card-body" });
		this.append(body, ...(children || []));
		if (opts.open === false) body.hidden = true;
		caret.addEventListener("click", () => {
			const open = body.hidden;
			body.hidden = !open;
			caret.classList.toggle("is-open", open);
			caret.setAttribute("aria-expanded", open ? "true" : "false");
			if (opts.onToggle) opts.onToggle(open);
		});
		this.append(wrap, head, body);
		wrap.cardBody = body;
		wrap.cardCaret = caret;
		return wrap;
	},

	// `polygon(rings, px)` lived here until M9.22 and is **deleted, not
	// deprecated**, for the same reason `placeholder()` below was: it had exactly
	// one caller — the zone detail's GEOFENCE section — and that section was
	// added scope the owner removed (`W2.11`). A helper with no caller that draws
	// something plausible is the kind of dead code that comes back by accident,
	// and this one also carried a `verify_bundle.py::REQUIRED` entry asserting a
	// global nothing used. Its arithmetic (a bounding box computed in data space,
	// with the y axis flipped because latitude grows northward and SVG y grows
	// down) is one `git show ac71e62:qhse/public/js/facility/render.js` away if a
	// zone map view ever wants it.

	// `placeholder(title, tranche)` lived here until M9.16 and is **deleted**, not
	// deprecated: it drew "this screen ships in tranche C, the route resolves
	// today", which is a true sentence only while a tranche is outstanding. Its
	// last caller went with M9.9c and the register recorded it as dead code
	// (`docs/FACILITY-DECISION-RECONCILIATION.md` `LS12`). A helper that renders
	// a promise is the one kind of dead code that can come back by accident.
};
